import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  FaArrowLeft,
  FaToggleOn,
  FaToggleOff,
  FaMapMarkerAlt,
  FaPhone,
  FaEnvelope,
  FaGlobe,
  FaFacebook,
  FaInstagram,
  FaTwitter,
  FaUsers,
  FaUserTimes,
  FaStar,
} from "react-icons/fa";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import axios from "axios";

// Fix for default markers in react-leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

const InstituteDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [institute, setInstitute] = useState(null);
  const [activeUsers, setActiveUsers] = useState([]);
  const [leftUsers, setLeftUsers] = useState([]);
  const [affiliationRequests, setAffiliationRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("details");

  useEffect(() => {
    fetchInstituteDetails();
    fetchInstituteUsers();
    fetchAffiliationRequests();
  }, [id]);

  const fetchInstituteDetails = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/institute/admin/${id}`,
        {
          withCredentials: true,
        }
      );
      setInstitute(response.data.institute);
    } catch (error) {
      console.error("Error fetching institute details:", error);
      setError("Failed to fetch institute details");
    }
  };

  const fetchInstituteUsers = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/institute/admin/${id}/users`,
        {
          withCredentials: true,
        }
      );
      setActiveUsers(response.data.activeUsers);
      setLeftUsers(response.data.leftUsers);
    } catch (error) {
      console.error("Error fetching institute users:", error);
    }
  };

  const fetchAffiliationRequests = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/institute/admin/${id}/affiliation-requests`,
        {
          withCredentials: true,
        }
      );
      setAffiliationRequests(response.data.requests);
    } catch (error) {
      console.error("Error fetching affiliation requests:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async () => {
    try {
      await axios.put(
        `${process.env.REACT_APP_BASE_URL}/api/institute/admin/${id}/status`,
        { is_enabled: !institute.is_enabled },
        {
          withCredentials: true,
        }
      );

      setInstitute((prev) => ({ ...prev, is_enabled: !prev.is_enabled }));
    } catch (error) {
      console.error("Error updating institute status:", error);
      setError("Failed to update institute status");
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-xl">Loading institute details...</div>
      </div>
    );
  }

  if (!institute) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-xl text-red-600">Institute not found</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col gap-4 md:flex-row md:justify-between md:items-center mb-8">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate("/institute-admin")}
              className="p-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
            >
              <FaArrowLeft />
            </button>
            {institute.logo && (
              <img
                src={institute.logo}
                alt={`${institute.name} logo`}
                className="w-12 h-12 rounded-full object-cover border-2 border-gray-200"
                onError={(e) => {
                  e.target.style.display = "none";
                }}
              />
            )}
            <h1 className="text-3xl font-bold text-gray-900">
              {institute.name}
            </h1>
            <span
              className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                institute.is_enabled
                  ? "bg-green-100 text-green-800"
                  : "bg-red-100 text-red-800"
              }`}
            >
              {institute.is_enabled ? "Enabled" : "Disabled"}
            </span>
          </div>
          <button
            onClick={handleToggleStatus}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium w-full md:w-auto justify-center ${
              institute.is_enabled
                ? "bg-red-600 hover:bg-red-700 text-white"
                : "bg-green-600 hover:bg-green-700 text-white"
            }`}
          >
            {institute.is_enabled ? <FaToggleOff /> : <FaToggleOn />}
            <span>{institute.is_enabled ? "Disable" : "Enable"}</span>
          </button>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
            {error}
          </div>
        )}

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="border-b border-gray-200">
            <nav className="flex flex-wrap gap-2 sm:space-x-8 px-6 overflow-x-auto">
              {[
                { key: "details", label: "Details", icon: FaStar },
                {
                  key: "users",
                  label: `Active Users (${activeUsers.length})`,
                  icon: FaUsers,
                },
                {
                  key: "leftUsers",
                  label: `Former Users (${leftUsers.length})`,
                  icon: FaUserTimes,
                },
                {
                  key: "requests",
                  label: `Requests (${affiliationRequests.length})`,
                  icon: FaEnvelope,
                },
              ].map(({ key, label, icon: Icon }) => (
                <button
                  key={key}
                  onClick={() => setActiveTab(key)}
                  className={`flex items-center space-x-2 py-4 border-b-2 font-medium text-sm ${
                    activeTab === key
                      ? "border-blue-500 text-blue-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  <Icon />
                  <span>{label}</span>
                </button>
              ))}
            </nav>
          </div>

          <div className="p-6">
            {/* Details Tab */}
            {activeTab === "details" && (
              <div className="space-y-6">
                {/* Basic Information */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900">
                      Basic Information
                    </h3>
                    <div className="bg-gray-50 p-4 rounded-lg space-y-3">
                      <div>
                        <label className="text-sm font-medium text-gray-500">
                          Institute Name
                        </label>
                        <p className="text-gray-900">{institute.name}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">
                          Type
                        </label>
                        <p className="text-gray-900 capitalize">
                          {institute.institute_type_name}
                        </p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">
                          Date Established
                        </label>
                        <p className="text-gray-900">
                          {formatDate(institute.date_established)}
                        </p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">
                          Contact Person
                        </label>
                        <p className="text-gray-900">
                          {institute.contact_person}
                        </p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">
                          Registration Number
                        </label>
                        <p className="text-gray-900">
                          {institute.company_registration_number ||
                            "Not provided"}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900">
                      Contact Information
                    </h3>
                    <div className="bg-gray-50 p-4 rounded-lg space-y-3">
                      <div className="flex items-center space-x-2">
                        <FaEnvelope className="text-gray-400" />
                        <span className="text-gray-900">{institute.email}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <FaPhone className="text-gray-400" />
                        <span className="text-gray-900">
                          {Array.isArray(institute.telephone)
                            ? institute.telephone.join(", ")
                            : institute.telephone || "Not provided"}
                        </span>
                      </div>
                      {institute.website && (
                        <div className="flex items-center space-x-2">
                          <FaGlobe className="text-gray-400" />
                          <a
                            href={institute.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:underline"
                          >
                            {institute.website}
                          </a>
                        </div>
                      )}
                      <div className="flex space-x-4">
                        {institute.facebook_url && (
                          <a
                            href={institute.facebook_url}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <FaFacebook className="text-blue-600 text-xl" />
                          </a>
                        )}
                        {institute.instagram_url && (
                          <a
                            href={institute.instagram_url}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <FaInstagram className="text-pink-600 text-xl" />
                          </a>
                        )}
                        {institute.x_url && (
                          <a
                            href={institute.x_url}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <FaTwitter className="text-blue-400 text-xl" />
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Address */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    Address & Location
                  </h3>
                  <div className="bg-gray-50 p-4 rounded-lg space-y-4">
                    <div className="flex items-start space-x-2">
                      <FaMapMarkerAlt className="text-gray-400 mt-1" />
                      <div>
                        <p className="text-gray-900">
                          {institute.plot_no && `${institute.plot_no}, `}
                          {institute.street && `${institute.street}, `}
                          {institute.area && `${institute.area}, `}
                          {institute.city}, {institute.country}
                        </p>
                        {institute.coordinates && (
                          <p className="text-gray-500 text-sm mt-1">
                            Coordinates: {institute.coordinates}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Map */}
                    {institute.coordinates && (
                      <div className="h-64 w-full rounded-lg overflow-hidden border border-gray-300">
                        <MapContainer
                          center={institute.coordinates
                            .split(",")
                            .map((coord) => parseFloat(coord.trim()))}
                          zoom={15}
                          style={{ height: "100%", width: "100%" }}
                        >
                          <TileLayer
                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                          />
                          <Marker
                            position={institute.coordinates
                              .split(",")
                              .map((coord) => parseFloat(coord.trim()))}
                          >
                            <Popup>
                              <div className="text-center">
                                <strong>{institute.name}</strong>
                                <br />
                                {institute.city}, {institute.country}
                              </div>
                            </Popup>
                          </Marker>
                        </MapContainer>
                      </div>
                    )}
                  </div>
                </div>

                {/* About */}
                {institute.about_institute && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      About
                    </h3>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-gray-900">
                        {institute.about_institute}
                      </p>
                    </div>
                  </div>
                )}

              </div>
            )}

            {/* Active Users Tab */}
            {activeTab === "users" && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Active Users
                </h3>
                {activeUsers.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Name
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Email
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Contact
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Age
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Joined
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {activeUsers.map((user) => (
                          <tr key={user.id}>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900">
                                {user.first_name} {user.middle_name}{" "}
                                {user.last_name}
                              </div>
                              <div className="text-sm text-gray-500">
                                @{user.user_name}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {user.email}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {user.contact}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {user.age} ({user.gender})
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {formatDate(user.joined_at)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-gray-500">No active users found.</p>
                )}
              </div>
            )}

            {/* Former Users Tab */}
            {activeTab === "leftUsers" && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Former Users
                </h3>
                {leftUsers.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Name
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Email
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Joined
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Left
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {leftUsers.map((user) => (
                          <tr key={user.id}>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900">
                                {user.first_name} {user.middle_name}{" "}
                                {user.last_name}
                              </div>
                              <div className="text-sm text-gray-500">
                                @{user.user_name}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {user.email}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {formatDate(user.joined_at)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {formatDate(user.left_at)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-gray-500">No former users found.</p>
                )}
              </div>
            )}

            {/* Affiliation Requests Tab */}
            {activeTab === "requests" && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Affiliation Requests
                </h3>
                {affiliationRequests.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            User
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Email
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Status
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Requested
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {affiliationRequests.map((request) => (
                          <tr key={request.id}>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900">
                                {request.first_name} {request.middle_name}{" "}
                                {request.last_name}
                              </div>
                              <div className="text-sm text-gray-500">
                                @{request.user_name}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {request.email}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span
                                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                  request.status === "pending"
                                    ? "bg-yellow-100 text-yellow-800"
                                    : request.status === "accepted"
                                    ? "bg-green-100 text-green-800"
                                    : "bg-red-100 text-red-800"
                                }`}
                              >
                                {request.status}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {formatDate(request.created_at)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-gray-500">
                    No affiliation requests found.
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InstituteDetails;
